import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { brandInfo, reviews, contactInfo, siteConfig } from '@/mocks/siteData';
import { JsonLd, getOrganizationSchema, getBreadcrumbSchema } from '@/components/base/JsonLd';

export default function AboutPage() {
  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: `关于${siteConfig.name}`,
    description: brandInfo.intro,
    url: `http://${siteConfig.domain}/about`,
    mainEntity: {
      '@type': 'Organization',
      name: siteConfig.name,
      description: brandInfo.intro,
      foundingDate: '2020',
      contactPoint: {
        '@type': 'ContactPoint',
        email: contactInfo.email,
        contactType: 'customer service',
      },
    },
  };

  const breadcrumbSchema = getBreadcrumbSchema([
    { name: '首页', url: `http://${siteConfig.domain}` },
    { name: '关于我们', url: `http://${siteConfig.domain}/about` },
  ]);

  const reviewSchema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: reviews.map((review, idx) => ({
      '@type': 'ListItem',
      position: idx + 1,
      item: {
        '@type': 'Review',
        reviewBody: review.content,
        author: {
          '@type': 'Person',
          name: review.name,
        },
        reviewRating: {
          '@type': 'Rating',
          ratingValue: review.rating,
          bestRating: 5,
        },
      },
    })),
  };

  return (
    <div className="min-h-screen bg-background-50">
      <JsonLd data={aboutSchema} />
      <JsonLd data={breadcrumbSchema} />
      <JsonLd data={reviewSchema} />
      <Navbar />

      {/* ... rest of main content same as before ... */}
      <main>
        {/* Hero Section */}
        <section className="relative w-full min-h-[420px] md:min-h-[500px] flex items-center">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Abstract%20Chinese%20ink%20wash%20art%20with%20warm%20crimson%20and%20gold%20gradients%2C%20elegant%20flowing%20brush%20strokes%2C%20traditional%20artistic%20style%20with%20modern%20minimalist%20touch%2C%20soft%20atmospheric%20texture%2C%20refined%20aesthetic%20composition&width=1800&height=900&seq=about-hero-bg&orientation=landscape"
              alt="关于五一吃瓜网"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/35 to-black/70"></div>
          </div>

          <div className="relative z-10 w-full px-4 md:px-6 pt-20">
            <div className="max-w-3xl mx-auto text-center">
              <p className="font-heading text-sm text-accent-300 mb-4 tracking-wider">ABOUT US</p>
              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-black text-white mb-4">
                关于<span className="text-primary-400">五一吃瓜网</span>
              </h1>
              <p className="text-base md:text-lg text-white/70 leading-relaxed max-w-2xl mx-auto">
                {brandInfo.intro}
              </p>
            </div>
          </div>
        </section>

        {/* Brand Features */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="px-4 md:px-6 max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-12">
              <div className="rounded-2xl p-8 md:p-10 bg-primary-500 text-white relative overflow-hidden">
                <div className="relative z-10">
                  <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center mb-6">
                    <i className="ri-fire-line text-white text-xl"></i>
                  </div>
                  <p className="font-heading text-5xl md:text-7xl font-black opacity-30 mb-4">95%</p>
                  <h3 className="font-heading text-2xl font-bold mb-3">用户满意度</h3>
                  <p className="text-white/80 text-sm leading-relaxed mb-6">
                    超过95%的吃瓜群众对我们的内容质量和更新速度表示满意，每天都有数以万计的忠实读者在这里获取最新爆料。
                  </p>
                  <p className="font-heading text-lg italic text-white/70">
                    "吃瓜，我们是认真的"
                  </p>
                </div>
                <div className="absolute top-0 right-0 w-48 h-48 rounded-full bg-white/5 -translate-y-1/2 translate-x-1/4"></div>
              </div>

              <div className="rounded-2xl p-8 md:p-10 bg-secondary-100 border border-secondary-200 relative overflow-hidden">
                <div className="relative z-10">
                  <div className="w-12 h-12 rounded-xl bg-secondary-200 flex items-center justify-center mb-6">
                    <i className="ri-group-line text-secondary-700 text-xl"></i>
                  </div>
                  <p className="font-heading text-5xl md:text-7xl font-black text-secondary-300 mb-4">500万+</p>
                  <h3 className="font-heading text-2xl font-bold text-foreground-900 mb-3">月活跃读者</h3>
                  <p className="text-foreground-600 text-sm leading-relaxed">
                    每月超过500万独立访客在五一吃瓜网浏览爆料内容，是国内最大的娱乐八卦聚合平台之一。
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {brandInfo.features.map((feature, idx) => (
                <div key={idx} className="rounded-xl p-6 bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300">
                  <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center mb-4">
                    <i className={`${['ri-timer-flash-line', 'ri-radar-line', 'ri-search-eye-line', 'ri-layout-masonry-line'][idx]} text-primary-600 text-lg`}></i>
                  </div>
                  <h4 className="font-heading text-base font-bold text-foreground-900 mb-2">{feature.title}</h4>
                  <p className="text-sm text-foreground-500 leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Reviews */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="px-4 md:px-6 max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-10 items-center">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-6">
                  <span className="w-2 h-2 rounded-full bg-foreground-900"></span>
                  <span className="text-xs font-semibold uppercase tracking-wider text-foreground-500">口碑评价</span>
                </div>
                <blockquote className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 leading-snug mb-6 max-w-lg">
                  <span className="text-primary-500 text-4xl md:text-5xl">"</span>
                  关注五一吃瓜网三年了，这是目前国内最靠谱的吃瓜平台。深度扒皮系列每期都追，从不让人失望！
                </blockquote>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-secondary-200 overflow-hidden">
                    <img src="https://readdy.ai/api/search-image?query=Middle%20aged%20Chinese%20person%20professional%20headshot%2C%20warm%20lighting%2C%20confident%20smile%2C%20clean%20cream%20background%2C%20editorial%20portrait%20photography&width=100&height=100&seq=about-review-avatar&orientation=squarish" alt="资深吃瓜群众" className="w-full h-full object-cover" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground-900">资深吃瓜群众</p>
                    <p className="text-xs text-accent-600 flex items-center gap-1">
                      <i className="ri-verified-badge-line"></i>十年吃瓜老用户
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex-1">
                <div className="aspect-[4/5] max-w-md mx-auto rounded-2xl overflow-hidden bg-background-100">
                  <img src="https://readdy.ai/api/search-image?query=Artistic%20black%20and%20white%20portrait%20of%20Chinese%20person%2C%20side%20profile%20looking%20upward%2C%20dramatic%20soft%20lighting%2C%20editorial%20fine%20art%20photography%2C%20clean%20white%20background%2C%20contemplative%20mood&width=600&height=750&seq=about-portrait-01&orientation=portrait" alt="吃瓜群众口碑" className="w-full h-full object-cover object-top" />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-14">
              {reviews.map((review, idx) => (
                <div key={idx} className="rounded-xl p-6 bg-background-100 border border-background-200/70">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-secondary-200 overflow-hidden flex-shrink-0">
                      <img src={review.avatar} alt={review.name} className="w-full h-full object-cover" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-900">{review.name}</p>
                      <div className="flex gap-0.5 mt-0.5">
                        {Array.from({ length: review.rating }).map((_, i) => (
                          <i key={i} className="ri-star-fill text-accent-500 text-xs"></i>
                        ))}
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">"{review.content}"</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact CTA */}
        <section className="relative w-full min-h-[400px] md:min-h-[480px] flex items-center">
          <div className="absolute inset-0">
            <img src="https://readdy.ai/api/search-image?query=Warm%20atmospheric%20Chinese%20night%20cityscape%20with%20red%20lanterns%2C%20modern%20and%20traditional%20fusion%2C%20golden%20neon%20lights%2C%20cinematic%20urban%20scene%2C%20soft%20bokeh%2C%20elegant%20mood%2C%20warm%20crimson%20and%20amber%20tones&width=1800&height=900&seq=about-cta-bg&orientation=landscape" alt="联系我们" className="w-full h-full object-cover object-center" />
            <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/40 to-black/75"></div>
          </div>
          <div className="relative z-10 w-full px-4 md:px-6">
            <div className="max-w-2xl mx-auto text-center">
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-black text-white mb-4">
                加入吃瓜<span className="text-primary-400">大家庭</span>
              </h2>
              <p className="text-base md:text-lg text-white/70 leading-relaxed mb-8">有料你就来！欢迎投稿爆料，一起吃遍天下瓜</p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-10">
                <div className="flex items-center gap-2 px-5 py-3 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-white">
                  <i className="ri-mail-line text-accent-400"></i>
                  <span className="text-sm">{contactInfo.email}</span>
                </div>
                <div className="flex items-center gap-2 px-5 py-3 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-white">
                  <i className="ri-wechat-line text-accent-400"></i>
                  <span className="text-sm">{contactInfo.wechat}</span>
                </div>
                <div className="flex items-center gap-2 px-5 py-3 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-white">
                  <i className="ri-weibo-line text-accent-400"></i>
                  <span className="text-sm">{contactInfo.weibo}</span>
                </div>
              </div>
              <a href={`mailto:${contactInfo.email}`} className="whitespace-nowrap inline-flex items-center gap-2 px-10 py-4 rounded-full bg-primary-500 text-white font-bold hover:bg-primary-600 transition-all duration-200 cursor-pointer text-sm">
                联系我们<i className="ri-arrow-right-up-line"></i>
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
